/**
 * Feature switches, as their own lightweight module.
 *
 * Same rationale as `titleCase.js` and `formatShipByDate.js` beside it: `common/utils.js` pulls in
 * pdfjs-dist, jspdf, zpl-renderer-js and export-to-csv, so a component that wants nothing but a
 * boolean must not import it. POM-6121 found that out the hard way — importing the flag from
 * `common/utils` into the Bulk Labels review tree took seven Jest suites down with
 * "Unexpected token 'export'" from export-to-csv, because none of them had reason to mock a
 * module they had never depended on.
 *
 * `common/utils.js` re-exports everything here, so existing importers are unaffected and there is
 * still exactly ONE definition of each flag.
 */

/**
 * POM-6083 — manual lot entry.
 *
 * Gated on its own rather than folded into the inventory switches because it is the only flow that
 * writes an UNVERIFIED claim: quantity recorded as dispensed that inventory never received. So it
 * rolls out on its own schedule.
 *
 * With this OFF, Fill Product behaves exactly as it did before POM-6083 (allocate from available
 * stock, or record an unassigned fill when there is none), and the bulk Review Lot & NDC picker
 * exactly as it did before POM-6121 (pick a stocked lot, or leave the row as it is).
 *
 * POM-6121 deliberately reuses this switch rather than adding one of its own: it is one capability
 * in two places, and two flags for one capability is how half of it ends up live.
 */
export const AddManualLotEnabled = process.env.REACT_APP_ADD_MANUAL_LOT_ENABLED === 'true' || false;

/**
 * POM-6121 — a label needs a lot that can actually be dispensed.
 *
 * Its OWN switch, and the one exception to the rule above, because it is not the same capability:
 * AddManualLotEnabled grants the ABILITY to record a lot by hand, this one turns on REFUSING to
 * print or fill an item that has no usable lot. The second has to follow the first by however
 * long operators need to get used to it, so they cannot share a flag.
 *
 * Governs both screens — the single-item Fill Product panel and the bulk Review Lot & NDC step —
 * so the behaviour can be stopped from one place.
 *
 * ON:  an expired, missing or absent lot takes the item out of the batch, and it cannot be put
 *      back; the single-item panel refuses an unassigned fill.
 * OFF: exactly the previous behaviour — a problem row can be printed anyway by unticking, a row
 *      with no lot prints and fills, and an unassigned fill is allowed.
 *
 * Defaults to ON when the variable is unset. An environment that forgets it then REFUSES rather
 * than silently shipping the old behaviour: a blocked operator is loud and gets fixed, a vial
 * going out mislabelled or unrecorded is quiet.
 */
export const BlockUnusableLotItems = process.env.REACT_APP_BLOCK_UNUSABLE_LOT_ITEMS !== 'false';

/**
 * Invoicing in the Admin Center — the Invoice Groups tile and every route beneath it (group list,
 * group invoices, create and edit invoice).
 *
 * A kill switch rather than an enable flag, so only the environment that wants invoicing hidden has
 * to set anything; unset means invoicing shows as before.
 *
 * 'true': the tile is hidden and the routes are refused, so the pages cannot be reached by URL
 * either. Otherwise the role check (`viewInvoiceGroups`) alone decides, as before.
 */
export const DisableInvoiceGroup = process.env.REACT_APP_DISABLE_INVOICE_GROUP === 'true' || false;

/**
 * POM-6400 — show the patient's default email / phone where a partner has none of its own.
 *
 * The backend's PARTNER_PATIENT_CONTACT_FALLBACK switch decides what is SENT; this one decides
 * what POM SHOWS for a partner's contact (order screen Patient card).
 *
 * ON:  a partner with no email or phone of its own shows the patient's default, with the Default
 *      chip.
 * OFF: it shows a dash. A partner's own value is shown either way.
 *
 * Off when unset, so only an environment that wants the default shown has to set anything.
 */
export const ShowDefaultPartnerContact =
  process.env.REACT_APP_SHOW_DEFAULT_PARTNER_CONTACT === 'true' || false;

export default AddManualLotEnabled;
